import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Eye, EyeOff, GripVertical, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/settings/settings-controls';
import { getGroupColor } from '@/lib/queues';
import { cn } from '@/lib/utils';

// One sortable entry-type row: name, the Visible/Hidden-to-doctor status chip, the show-to-doctor
// toggle, and delete. showToDoctor is the inverse of "not-show-to-doctor" (§6.1).
export function EntryTypeRow({ type, disabled = false, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: type.id,
  });
  const visible = Boolean(type.showToDoctor);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 shadow-sm',
        isDragging ? 'opacity-60 shadow-md ring-2 ring-ring/50' : '',
      )}
    >
      <button
        type="button"
        aria-label="Drag to reorder entry type"
        {...attributes}
        {...listeners}
        className="shrink-0 cursor-grab rounded text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      <Input
        value={type.name}
        onChange={(e) => onChange({ name: e.target.value })}
        maxLength={60}
        disabled={disabled}
        className="h-8 w-64 text-sm font-medium"
        placeholder="Entry type"
      />
      <div className="flex-1" />
      {visible ? (
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-semibold',
            getGroupColor('emerald').chipClassName,
          )}
        >
          <Eye className="h-3 w-3" aria-hidden />
          Visible to doctor
        </span>
      ) : (
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-semibold',
            getGroupColor('amber').chipClassName,
          )}
        >
          <EyeOff className="h-3 w-3" aria-hidden />
          Hidden from doctor
        </span>
      )}
      <label className="flex shrink-0 flex-col items-start gap-1">
        <span className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
          Default duration (min)
        </span>
        <Input
          type="number"
          min={0}
          value={type.defaultVisitMinutes ?? ''}
          onChange={(e) =>
            onChange({ defaultVisitMinutes: e.target.value === '' ? null : Number(e.target.value) })
          }
          disabled={disabled}
          placeholder="15"
          className="h-8 w-16 text-sm"
        />
      </label>
      <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-foreground">
        Show to doctor
        <Switch
          checked={visible}
          onChange={(v) => onChange({ showToDoctor: v })}
          label={`Show ${type.name} to doctor`}
          disabled={disabled}
        />
      </span>
      <button
        type="button"
        onClick={onDelete}
        disabled={disabled}
        aria-label={`Delete entry type ${type.name}`}
        className={cn(
          'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent text-muted-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          disabled
            ? 'cursor-not-allowed opacity-50'
            : 'hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive',
        )}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}
